import React, { useState, useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Play, Stop, Refresh, Trash } from "iconsax-react";
import AudioPlayer from "./AudioPlayer";

const formatTime = (seconds) => {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
};

const AudioRecorder = ({ onRecordingComplete, onCancel, onRecordingReset }) => {
  const theme = useTheme();
  const [recording, setRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const intervalRef = useRef(null);
  const streamRef = useRef(null);
  /** Elapsed whole seconds while recording (for inbox duration label before upload). */
  const recordingElapsedSecRef = useRef(0);

  const handleStartRecording = async () => {
    try {
      // START TIMER IMMEDIATELY - before any async operations
      setTimer(0);
      setRecording(true);
      audioChunksRef.current = [];
      recordingElapsedSecRef.current = 0;

      // Start the visual timer right away
      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          const next = prev + 1;
          recordingElapsedSecRef.current = next;
          return next;
        });
      }, 1000);

      // Get audio stream with echo cancellation enabled
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        }
      });

      streamRef.current = stream;

      // Determine the best supported audio format
      // NOTE: Most browsers only support audio/webm with MediaRecorder
      // We'll record as WebM and convert to MP3 on the server using Cloudinary
      let mimeType = 'audio/webm';
      let fileExtension = 'webm';
      
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
        fileExtension = 'webm';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
        fileExtension = 'webm';
      }
      
      console.log(`Recording in ${mimeType} format`);

      // Create MediaRecorder with optimal settings
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000, // 128 kbps for good quality
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        console.log(`Audio data available: ${event.data.size} bytes`);
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        console.log(`Recording stopped. Audio chunks collected: ${audioChunksRef.current.length}`);
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          console.log(`Audio blob created. Size: ${audioBlob.size} bytes, Type: ${audioBlob.type}`);
          const file = new File([audioBlob], `recording.${fileExtension}`, { type: mimeType });
          setRecordedAudio(URL.createObjectURL(file));
          const durationSeconds = Math.max(0, recordingElapsedSecRef.current);
          onRecordingComplete(file, { durationSeconds });
        } else {
          console.error('No audio data recorded - chunks array is empty');
          console.error('MediaRecorder state:', mediaRecorderRef.current?.state);
          setRecording(false);
        }
        
        // Stop all tracks to release the microphone
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        console.error('Error event:', event);
      };

      // Start recording without timeslice (let browser manage data chunks automatically)
      mediaRecorder.start();
      console.log('MediaRecorder started');
      
    } catch (error) {
      console.error("Failed to start recording:", error);
      clearInterval(intervalRef.current);
      setRecording(false);
      setTimer(0);
      recordingElapsedSecRef.current = 0;
      alert("Failed to access microphone. Please check your permissions.");
    }
  };

  const handleStopRecording = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      clearInterval(intervalRef.current);
      setRecording(false);
    } catch (error) {
      console.error("Failed to stop recording:", error);
    }
  };

  const handleRestart = () => {
    // Stop recording if in progress
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    // Stop all tracks
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    
    clearInterval(intervalRef.current);
    setRecording(false);
    setTimer(0);
    recordingElapsedSecRef.current = 0;
    setRecordedAudio(null);
    onRecordingReset();
  };

  const handleDeleteRecording = () => {
    handleRestart();
    onCancel?.();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
        gap: { xs: 0.25, sm: 0.5 },
        borderRadius: 5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {recordedAudio && <AudioPlayer audioSrc={recordedAudio} />}
      </Box>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          justifyContent: "center",
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.common.white,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 5,
          paddingX: { xs: 0.25, sm: 1 },
        })}
      >
        <IconButton
          onClick={handleDeleteRecording}
          color="error"
          size="small"
          sx={{ p: { xs: 0.5, sm: 1 } }}
        >
          <Trash size="20" variant="Linear" color={theme.palette.error.main} />
        </IconButton>

        {(recording || !recordedAudio) && (
          <Typography
            variant="body1"
            sx={(theme) => ({
              minWidth: { xs: 34, sm: 40 },
              textAlign: "center",
              fontSize: { xs: "0.75rem", sm: "1rem" },
              px: { xs: 0.25, sm: 0 },
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            })}
          >
            {formatTime(timer)}
          </Typography>
        )}

        {!recording && !recordedAudio && (
          <>
            <IconButton onClick={handleStartRecording} color="primary" size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
              <Play size="20" variant="Bold" color={theme.palette.primary.main} />
            </IconButton>
            <IconButton
              onClick={handleRestart}
              color="default"
              size="small"
              sx={(theme) => ({
                p: { xs: 0.5, sm: 1 },
                color: theme.palette.text.primary,
              })}
            >
              <Refresh size="20" variant="Linear" color={theme.palette.text.primary} />
            </IconButton>
          </>
        )}

        {recording && (
          <>
            <IconButton onClick={handleStopRecording} color="error" size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
              <Stop size="20" variant="Linear" color={theme.palette.error.main} />
            </IconButton>
            <IconButton
              onClick={handleRestart}
              color="default"
              size="small"
              sx={(theme) => ({
                p: { xs: 0.5, sm: 1 },
                color: theme.palette.text.primary,
              })}
            >
              <Refresh size="20" variant="Linear" color={theme.palette.text.primary} />
            </IconButton>
          </>
        )}

        {!recording && recordedAudio && (
          <IconButton
            onClick={handleRestart}
            color="default"
            size="small"
            sx={(theme) => ({
              p: { xs: 0.5, sm: 1 },
              color: theme.palette.text.primary,
            })}
          >
            <Refresh size="20" variant="Linear" color={theme.palette.text.primary} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default AudioRecorder;
